import { api } from './auth';

/**
 * Import status constants
 */
export const ImportStatus = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
};

/**
 * Upload a CSV file for import
 * @param {File} file - The CSV file to upload
 * @returns {Promise} - Response with file_id, headers, preview_rows, total_rows
 */
export const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post('/imports/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

/**
 * Get user's saved mapping preferences
 * @returns {Promise} - Response with mapping object
 */
export const getMappingPreferences = async () => {
  const response = await api.get('/imports/mapping-preferences');
  return response.data;
};

/**
 * Save user's mapping preferences
 * @param {Object} mapping - Field mapping object (CSV column -> profile field)
 * @returns {Promise} - Success message
 */
export const saveMappingPreferences = async (mapping) => {
  const response = await api.post('/imports/mapping-preferences', { mapping });
  return response.data;
};

/**
 * Validate field mapping
 * @param {Object} mapping - Field mapping object to validate
 * @returns {Promise} - Validation result with valid flag and errors array
 */
export const validateMapping = async (mapping) => {
  const response = await api.post('/imports/validate', { mapping });
  return response.data;
};

/**
 * Get preview of mapped contacts
 * @param {string} fileId - The file ID from upload
 * @param {Object} mapping - Field mapping object
 * @returns {Promise} - Response with contacts array and total count
 */
export const getPreview = async (fileId, mapping) => {
  const response = await api.post('/imports/preview', {
    file_id: fileId,
    mapping,
  });
  return response.data;
};

/**
 * Execute the import (starts background processing)
 * @param {string} fileId - The file ID from upload
 * @param {Object} mapping - Field mapping object
 * @returns {Promise} - Response with import_id for status tracking
 */
export const executeImport = async (fileId, mapping) => {
  const response = await api.post('/imports/execute', {
    file_id: fileId,
    mapping,
  });
  return response.data;
};

/**
 * Get import status for progress tracking
 * @param {number} importId - The import history record ID
 * @returns {Promise} - Response with current progress and status
 */
export const getImportStatus = async (importId) => {
  const response = await api.get(`/imports/status/${importId}`);
  return response.data;
};

/**
 * Cancel a running import
 * @param {number} importId - The import history record ID
 * @returns {Promise} - Success message
 */
export const cancelImport = async (importId) => {
  const response = await api.post(`/imports/cancel/${importId}`);
  return response.data;
};

/**
 * Poll import status until completion
 * @param {number} importId - The import history record ID
 * @param {Function} onProgress - Callback for progress updates
 * @param {number} interval - Polling interval in ms (default 1000)
 * @returns {Promise} - Final import status
 */
export const pollImportStatus = async (importId, onProgress, interval = 1000) => {
  return new Promise((resolve, reject) => {
    const poll = async () => {
      try {
        const status = await getImportStatus(importId);
        
        // Call progress callback
        if (onProgress) {
          onProgress(status);
        }
        
        // Check if import is complete
        if (
          status.status === ImportStatus.COMPLETED ||
          status.status === ImportStatus.FAILED ||
          status.status === ImportStatus.CANCELLED
        ) {
          resolve(status);
          return;
        }
        
        // Continue polling
        setTimeout(poll, interval);
      } catch (error) {
        reject(error);
      }
    };
    
    poll();
  });
};

/**
 * Get user's import history
 * @returns {Promise} - Response with array of import history records
 */
export const getImportHistory = async () => {
  const response = await api.get('/imports/history');
  return response.data;
};

/**
 * Download original CSV file from import history
 * @param {number} historyId - The import history record ID
 * @returns {Promise} - Blob for file download
 */
export const downloadImportFile = async (historyId) => {
  const response = await api.get(`/imports/history/${historyId}/download`, {
    responseType: 'blob',
  });
  
  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  
  // Get filename from Content-Disposition header if available
  const contentDisposition = response.headers['content-disposition'];
  let filename = 'import.csv';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }
  
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  
  return response.data;
};
